import { useEffect, useState } from "react";
import { ExploreOutlinedIcon } from "src/components";

export default function ExplorerPlusIcon({ 
  tier = "ambassador", // "plus" | "ambassador"
  variant = "animated", // "animated" | "static" | "hover"
  size = 44,
  backgroundColor, // optional override
  backgroundStyle = "circle" // "circle" | "square" | "rounded" | "none"
}) {
  const [animate, setAnimate] = useState(false);

  // Auto-animation for animated variant
  useEffect(() => {
    if (variant !== "animated") return;

    const initialTimeout = setTimeout(() => setAnimate(true), 300);
    const interval = setInterval(() => setAnimate(true), 10000 + Math.random() * 2000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [variant]);

  // Reset animation state
  useEffect(() => {
    if (animate) {
      const timeout = setTimeout(() => setAnimate(false), 1400);
      return () => clearTimeout(timeout);
    }
  }, [animate]);

  // Tier-based styling
  const tierColors = {
    ambassador: {
      primary: "#D4AF37",      // gold compass
      secondary: "#FFA500",
      glow: "rgba(255, 200, 80, 0.5)",
      background: "#FFFFFF"    // soft cream
    },
    plus: {
      primary: "#A9A9A9",     // silver compass
      secondary: "#E5E5E5", 
      glow: "rgba(192, 192, 192, 0.8)",
      background: "#F7F7F7"    // light grey
    }
  };

  const colors = tierColors[tier];
  const bgColor = backgroundColor || colors.background;
  const shouldAnimate = animate || variant === "hover";

  // Background style
  const getBackgroundStyle = () => {
    if (backgroundStyle === "none" || bgColor === "transparent") return {};

    const baseStyle = {
      backgroundColor: bgColor,
      position: "absolute",
      top: 0,
      left: 0,
      width: size,
      height: size,
      zIndex: 1
    };

    switch (backgroundStyle) {
      case "circle": return { ...baseStyle, borderRadius: "50%" };
      case "square": return baseStyle;
      case "rounded": return { ...baseStyle, borderRadius: size * 0.2 };
      default: return { ...baseStyle, borderRadius: "50%" };
    }
  };

  return (
    <div 
      className={`premium-compass-container ${variant === "hover" ? "hover-trigger" : ""} ${shouldAnimate ? "animate" : ""}`}
      style={{ 
        width: size, 
        height: size,
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      {/* Background */}
      {bgColor !== "transparent" && backgroundStyle !== "none" && (
        <div className="background-layer" style={getBackgroundStyle()} />
      )}

      {/* Outer glow ring */}
      <div 
        className={`glow-ring ${shouldAnimate ? "animate" : ""}`}
        style={{
          position: "absolute",
          width: size * 1.6,
          height: size * 1.6,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`,
          opacity: shouldAnimate ? 1 : 0,
          zIndex: 0
        }}
      />

      {/* Main compass icon */}
      <ExploreOutlinedIcon
        sx={{
          fontSize: size,
          color: colors.primary,
          filter: `drop-shadow(0 0 ${size * 0.15}px ${colors.glow}) drop-shadow(0 0 ${size * 0.3}px ${colors.secondary})`,
          position: "relative",
          zIndex: 2
        }}
      />

      {/* Shimmer overlay */}
      <div 
        className={`shimmer-overlay ${shouldAnimate ? "animate" : ""}`}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: size,
          height: size,
          borderRadius: backgroundStyle === "circle" ? "50%" : 
                      backgroundStyle === "rounded" ? size * 0.2 : 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 3
        }}
      >
        <div 
          className="shimmer-sweep"
          style={{
            position: "absolute",
            top: "-10%",
            left: "-100%",
            width: "120%",
            height: "120%",
            background: `linear-gradient(
              45deg, 
              transparent 30%, 
              rgba(255, 255, 255, 0.8) 50%, 
              transparent 70%
            )`,
            transform: "rotate(45deg)"
          }}
        />
      </div>

      <style>{`
        /* Animate the whole component */
        .premium-compass-container.animate {
          animation: premium-compass-spin 1.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* Glow, shimmer still animate independently */
        .glow-ring.animate { animation: glow-pulse 1.4s ease-out; }
        .shimmer-overlay.animate .shimmer-sweep { animation: shimmer-sweep 0.8s ease-in-out 0.3s; }

        /* Hover trigger support */
        .premium-compass-container.hover-trigger:hover .glow-ring,
        .premium-compass-container.hover-trigger:hover .shimmer-overlay {
          animation-play-state: running;
        }

        @keyframes premium-compass-spin {
          0%, 100% { transform: translateY(0) scale(1) rotate(0deg); }
          25% { transform: translateY(-6px) scale(1.15) rotate(90deg); }
          50% { transform: translateY(-4px) scale(1.1) rotate(180deg); }
          75% { transform: translateY(-2px) scale(1.05) rotate(270deg); }
        }

        @keyframes glow-pulse {
          0% { opacity: 0; transform: scale(0.8); }
          30% { opacity: 1; transform: scale(1.2); }
          70% { opacity: 0.8; transform: scale(1.4); }
          100% { opacity: 0; transform: scale(1.6); }
        }

        @keyframes shimmer-sweep {
          0%   { transform: rotate(45deg) translateX(-150%); opacity: 0; }
          25%  { opacity: 0.4; }
          50%  { transform: rotate(45deg) translateX(0); opacity: 0.8; }
          75%  { opacity: 0.4; }
          100% { transform: rotate(45deg) translateX(150%); opacity: 0; }
        }     

        @media (prefers-reduced-motion: reduce) {
          .premium-compass-container.animate { animation: gentle-glow 1s ease-in-out; }
          .glow-ring.animate,
          .particle.animate,
          .shimmer-overlay.animate .shimmer-sweep { animation: none; }
        }

        @keyframes gentle-glow {
          0%, 100% { transform: translateY(0) scale(1) rotate(0deg); }
          50% { transform: translateY(-2px) scale(1.05) rotate(0deg); }
        }
      `}</style>
    </div>
  );
}
